import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, BarChart3, Clock, FileText, UserPlus, ArrowLeft, FolderPlus, DollarSign, Edit, Shield, Database } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useManagerStats } from '@/hooks/useManagerStats';
import { BillingView } from './BillingView';
import { ManualTimeEntry } from './ManualTimeEntry';
import { RoleManagement } from './RoleManagement';

export const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { stats, loading } = useManagerStats();
  const { isAdmin } = useUserRole();
  const [currentView, setCurrentView] = useState<'dashboard' | 'billing' | 'manual-entry' | 'role-management'>('dashboard');

  if (currentView === 'billing') {
    return <BillingView onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'manual-entry') {
    return <ManualTimeEntry onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'role-management') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentView('dashboard')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Rôles</h1>
              <p className="text-muted-foreground">Gérer les rôles et permissions des utilisateurs</p>
            </div>
          </div>
          <RoleManagement />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-8">
            <div className="text-lg">Chargement des statistiques...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tableau de bord Manager</h1>
              <p className="text-muted-foreground">Gestion et supervision des équipes</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold">{stats.activeEmployees}</div>
              <div className="text-sm text-muted-foreground">Employés actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <div className="text-2xl font-bold">{stats.totalHoursThisWeek.toFixed(2)}h</div>
              <div className="text-sm text-muted-foreground">Heures cette semaine</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-orange-600" />
              <div className="text-2xl font-bold">{stats.pendingTimesheets}</div>
              <div className="text-sm text-muted-foreground">Feuilles de temps en attente</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Employee Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion des Employés
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Gérer les profils, horaires et permissions des employés
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => navigate('/employees')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Liste des employés
                </Button>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => navigate('/employees?create=1')}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Ajouter un employé
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setCurrentView('role-management')}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Gestion des rôles
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Project Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderPlus className="h-5 w-5" />
                Gestion des Projets
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Créer et gérer les projets de l'entreprise
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => navigate('/projects')}
                >
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Gestion des projets
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Billing Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Facturation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Gérer la facturation des heures travaillées par projet et semaine
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setCurrentView('billing')}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Vue facturation
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Time Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Gestion du Temps
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Superviser les heures travaillées et gérer les entrées de temps
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setCurrentView('manual-entry')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Entrée de temps manuelle
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => navigate('/timesheets')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Feuilles de temps
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  Planning hebdomadaire
                  </Button>

              </div>
            </CardContent>
          </Card>

          {/* Migration de données (admin) */}
          {isAdmin() && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Migration de données
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Importer, mapper et préparer les anciennes données avant migration
              </p>
              <div className="space-y-2">
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => navigate('/data-migration-workflow')}
                >
                  <Database className="h-4 w-4 mr-2" />
                  Workflow de migration complet
                </Button>
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  onClick={() => navigate('/data-mapping')}
                >
                  <Database className="h-4 w-4 mr-2" />
                  Mapping des champs
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Reports */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Rapports et Analyses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Consulter les statistiques et générer des rapports
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => navigate('/weekly-report')}
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Rapport hebdomadaire
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => navigate('/monthly-report')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Rapport mensuel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
